<template>
  <Modal
    v-model="visibleSync"
    :title="modalTitle"
    class-name="intent-collection-field-modal"
    width="520"
    @on-visible-change="onVisibleChange"
  >
    <div class="intent-modal-form">
      <div class="intent-modal-field">
        <label class="intent-modal-field__label">
          <span class="intent-modal-field__required">*</span>
          名称
        </label>
        <Input
          v-model="form.name"
          class="intent-modal-field__input"
          placeholder="如：是否有确认尾号"
          :maxlength="80"
        />
      </div>

      <div class="intent-modal-field-row">
        <div class="intent-modal-field intent-modal-field--type">
          <label class="intent-modal-field__label">
            <span class="intent-modal-field__required">*</span>
            类型
          </label>
          <Select
            v-model="form.type"
            class="intent-modal-field__input"
            transfer
            @on-change="onTypeChange"
          >
            <Option
              v-for="opt in fieldTypeOptions"
              :key="opt.value"
              :value="opt.value"
              :label="opt.label"
            />
          </Select>
        </div>
        <div class="intent-modal-field intent-modal-field--collect">
          <label class="intent-modal-field__label">
            <span class="intent-modal-field__required">*</span>
            是否在表单展示
          </label>
          <Select
            v-model="form.collectForm"
            class="intent-modal-field__input"
            transfer
          >
            <Option
              v-for="opt in collectFormOptions"
              :key="opt.value"
              :value="opt.value"
              :label="opt.label"
            />
          </Select>
        </div>
      </div>

      <div v-if="form.type === EXTRACT_FIELD_TYPE.ENUM" class="intent-modal-field">
        <div class="intent-modal-field__label-row">
          <label class="intent-modal-field__label intent-modal-field__label--inline">
            <span class="intent-modal-field__required">*</span>
            允许的值
          </label>
          <span class="intent-modal-field__hint intent-modal-field__hint--inline">
            （指定后，大模型只能从这些选项中选择）
          </span>
        </div>
        <div class="intent-modal-field__control">
          <div class="enum-tag-input__wrap">
            <Input
              v-model="enumInput"
              class="enum-tag-input__field"
              placeholder="输入后回车添加，如：是"
              :disabled="isAllowedValuesFull"
              @on-enter="addEnumValue"
            />
            <span class="enum-tag-input__count">
              {{ form.allowedValues.length }}/{{ EXTRACT_ALLOWED_VALUES_MAX_COUNT }}
            </span>
          </div>
          <div v-if="form.allowedValues.length" class="enum-tag-input__tags">
            <Tag
              v-for="(item, index) in form.allowedValues"
              :key="'enum-' + index + '-' + item"
              closable
              color="primary"
              class="enum-value-tag"
              @on-close="removeEnumValue(index)"
            >
              {{ item }}
            </Tag>
          </div>
        </div>
      </div>

      <div class="intent-modal-field">
        <label class="intent-modal-field__label">
          <span class="intent-modal-field__required">*</span>
          抽取说明
        </label>
        <div class="intent-modal-field__control">
          <Input
            v-model="form.extractInstruction"
            type="textarea"
            :rows="5"
            :maxlength="EXTRACT_INSTRUCTION_MAX_LENGTH"
            placeholder="说明如何从对话中判定该字段的值，例如尾号三态规则..."
          />
          <div class="extract-instruction-meta">
            <span class="extract-instruction-meta__text">
              {{ extractInstructionLength }}/{{ EXTRACT_INSTRUCTION_MAX_LENGTH }}
              · 仅用于填表抽取，不参与定级
            </span>
          </div>
        </div>
      </div>
    </div>

    <div slot="footer" class="intent-modal-footer">
      <Button @click="close">取消</Button>
      <Button type="primary" @click="submit">保存</Button>
    </div>
  </Modal>
</template>

<script>
import {
  EXTRACT_FIELD_TYPE,
  EXTRACT_FIELD_TYPE_OPTIONS,
  EXTRACT_ALLOWED_VALUES_MAX_COUNT,
  EXTRACT_INSTRUCTION_MAX_LENGTH,
} from '../../constants/intentTag'
import { generateExtractFieldId, isExtractFieldCollectForm } from '../../utils/intentTagHelpers'

function createEmptyForm() {
  return {
    fieldId: '',
    name: '',
    type: EXTRACT_FIELD_TYPE.ENUM,
    allowedValues: [],
    extractInstruction: '',
    collectForm: 'yes',
  }
}

export default {
  name: 'CollectionFieldModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editIndex: {
      type: Number,
      default: -1,
    },
    initialData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      EXTRACT_FIELD_TYPE,
      EXTRACT_ALLOWED_VALUES_MAX_COUNT,
      EXTRACT_INSTRUCTION_MAX_LENGTH,
      fieldTypeOptions: EXTRACT_FIELD_TYPE_OPTIONS,
      collectFormOptions: [
        { value: 'yes', label: '是' },
        { value: 'no', label: '否' },
      ],
      form: createEmptyForm(),
      enumInput: '',
    }
  },
  computed: {
    visibleSync: {
      get() {
        return this.value
      },
      set(next) {
        this.$emit('input', next)
      },
    },
    modalTitle() {
      return this.editIndex >= 0 ? '编辑采集字段' : '添加采集字段'
    },
    extractInstructionLength() {
      return String(this.form.extractInstruction || '').length
    },
    isAllowedValuesFull() {
      return this.form.allowedValues.length >= EXTRACT_ALLOWED_VALUES_MAX_COUNT
    },
  },
  methods: {
    onVisibleChange(open) {
      if (!open) return
      this.resetForm()
    },
    resetForm() {
      const src = this.initialData && typeof this.initialData === 'object' ? this.initialData : null
      if (src) {
        this.form = {
          fieldId: src.fieldId || generateExtractFieldId(),
          name: src.name != null ? String(src.name) : '',
          type: src.type || EXTRACT_FIELD_TYPE.ENUM,
          allowedValues: Array.isArray(src.allowedValues) ? [...src.allowedValues] : [],
          extractInstruction:
            src.extractInstruction != null ? String(src.extractInstruction) : '',
          collectForm: isExtractFieldCollectForm(src) ? 'yes' : 'no',
        }
      } else {
        this.form = {
          ...createEmptyForm(),
          fieldId: generateExtractFieldId(),
        }
      }
      this.enumInput = ''
    },
    onTypeChange(type) {
      if (type !== EXTRACT_FIELD_TYPE.ENUM) {
        this.form.allowedValues = []
        this.enumInput = ''
      }
    },
    addEnumValue() {
      const value = String(this.enumInput || '').trim()
      if (!value) return
      if (this.isAllowedValuesFull) {
        this.$Message.warning(`最多添加 ${EXTRACT_ALLOWED_VALUES_MAX_COUNT} 个允许的值`)
        return
      }
      if (this.form.allowedValues.includes(value)) {
        this.enumInput = ''
        return
      }
      this.form.allowedValues.push(value)
      this.enumInput = ''
    },
    removeEnumValue(index) {
      this.form.allowedValues.splice(index, 1)
    },
    close() {
      this.visibleSync = false
    },
    submit() {
      const name = String(this.form.name || '').trim()
      if (!name) {
        this.$Message.warning('请填写名称')
        return
      }
      if (this.form.type === EXTRACT_FIELD_TYPE.ENUM && !this.form.allowedValues.length) {
        this.$Message.warning('请至少添加一个允许的值')
        return
      }
      if (
        this.form.type === EXTRACT_FIELD_TYPE.ENUM &&
        this.form.allowedValues.length > EXTRACT_ALLOWED_VALUES_MAX_COUNT
      ) {
        this.$Message.warning(`允许的值最多 ${EXTRACT_ALLOWED_VALUES_MAX_COUNT} 条`)
        return
      }
      const extractInstruction = String(this.form.extractInstruction || '').trim()
      if (!extractInstruction) {
        this.$Message.warning('请填写抽取说明')
        return
      }
      this.$emit('submit', {
        fieldId: this.form.fieldId || generateExtractFieldId(),
        name,
        type: this.form.type,
        allowedValues:
          this.form.type === EXTRACT_FIELD_TYPE.ENUM ? [...this.form.allowedValues] : [],
        extractInstruction,
        collectForm: this.form.collectForm !== 'no',
      })
      this.visibleSync = false
    },
  },
}
</script>

<style lang="scss" scoped>
.intent-modal-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.intent-modal-field-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;

  .intent-modal-field {
    min-width: 0;
  }

  .intent-modal-field--type {
    flex: 7 1 0;
  }

  .intent-modal-field--collect {
    flex: 3 1 0;
  }
}

.intent-modal-field {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
}

.intent-modal-field__label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #262626;
  font-weight: 400;
}

.intent-modal-field__required {
  margin-right: 2px;
  color: #ed4014;
}

.intent-modal-field__control,
.intent-modal-field__input {
  width: 100%;
}

.intent-modal-field__label-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0 8px;
  margin-bottom: 8px;
}

.intent-modal-field__label--inline {
  margin-bottom: 0;
  flex-shrink: 0;
}

.intent-modal-field__hint--inline {
  margin: 0;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.5;
  font-weight: 400;
}

.enum-tag-input__wrap {
  position: relative;
  width: 100%;
}

.enum-tag-input__wrap ::v-deep .enum-tag-input__field .ivu-input {
  padding-right: 52px;
}

.enum-tag-input__count {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #bfbfbf;
  line-height: 1;
  pointer-events: none;
  user-select: none;
}

.enum-tag-input__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.enum-tag-input__tags ::v-deep .enum-value-tag.ivu-tag {
  display: inline-flex !important;
  align-items: center;
  vertical-align: middle;
  max-width: 100%;
  min-width: 0;
  height: 18px;
  line-height: 16px;
  padding: 0 4px !important;
  margin: 0 !important;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 400;
  border: 1px solid #bae7ff !important;
  background: #f0f9ff !important;
  color: #1890ff !important;
  box-sizing: border-box;
}

.enum-tag-input__tags ::v-deep .enum-value-tag.ivu-tag .ivu-tag-text,
.enum-tag-input__tags ::v-deep .enum-value-tag.ivu-tag .ivu-tag-text.ivu-tag-color-white {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px !important;
  font-weight: 400 !important;
  line-height: 16px !important;
  color: #1890ff !important;
}

.enum-tag-input__tags ::v-deep .enum-value-tag.ivu-tag .ivu-icon-ios-close {
  flex-shrink: 0;
  margin-left: 1px;
  color: #91d5ff !important;
  opacity: 1 !important;
  font-size: 11px !important;
  font-weight: 400;
  top: 0 !important;
}

.enum-tag-input__tags ::v-deep .enum-value-tag.ivu-tag .ivu-icon-ios-close:hover {
  color: #1890ff !important;
}

.extract-instruction-meta {
  margin-top: 6px;
}

.extract-instruction-meta__text {
  font-size: 12px;
  color: #bfbfbf;
  line-height: 1.5;
}

.intent-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
</style>

<style lang="scss">
.intent-collection-field-modal {
  .ivu-modal-body {
    padding: 20px 24px 8px;
  }

  .ivu-modal-footer {
    border-top: none;
    padding: 8px 24px 20px;

    button + button {
      margin-left: 0;
    }
  }
}
</style>
